<script setup lang="ts">
/**
 * "Save your writing first": exports the whole corpus before the account goes,
 * or lets the user say they don't need a copy. `ready` turns true once either
 * is done. Markdown comes first because it opens anywhere; a .sei file is only
 * useful for coming back to Superelectric Ink.
 */
import { computed, ref } from 'vue'
import MoireCanvas from './MoireCanvas.vue'
import { exportCorpusAsMarkdown, exportCorpusToFile, type TransferProgress } from '../services/seiExport'

const props = defineProps<{ editor?: any; disabled?: boolean }>()
const ready = defineModel<boolean>('ready', { default: false })

const exporting = ref<'markdown' | 'sei' | null>(null)
const progress = ref<TransferProgress | null>(null)
const savedPath = ref<string | null>(null)
const leftOut = ref<string[]>([])
const exportError = ref<string | null>(null)
const skipped = ref(false)
// The Platen strip: every progress report feeds the paper half a line, and the
// moiré turns that small real movement into a sweep across the whole strip.
const feed = ref(0)

const progressLabel = computed(() => {
  const value = progress.value
  if (!value) return 'Exporting…'
  return value.total ? `${value.step} (${value.current} of ${value.total})…` : `${value.step}…`
})

function update() {
  ready.value = savedPath.value !== null || skipped.value
}

async function handleExport(kind: 'markdown' | 'sei') {
  exportError.value = null
  exporting.value = kind
  feed.value = 0
  const onProgress = (value: TransferProgress) => {
    progress.value = value
    feed.value += 3
  }
  try {
    const saved = kind === 'markdown'
      ? await exportCorpusAsMarkdown(props.editor, onProgress)
      : await exportCorpusToFile(props.editor, { includeHistory: true, includeFonts: true, password: null }, onProgress)
    if (saved) {
      savedPath.value = saved.path
      leftOut.value = 'unreadable' in saved ? saved.unreadable : []
    }
  } catch (error) {
    exportError.value = error instanceof Error ? error.message : String(error)
  } finally {
    exporting.value = null
    progress.value = null
    update()
  }
}
</script>

<template>
  <div class="export-step">
    <div class="export-row">
      <button type="button" class="s-btn" :disabled="exporting !== null || disabled" @click="handleExport('markdown')">
        {{ exporting === 'markdown' ? progressLabel : 'Export as Markdown (.zip)…' }}
      </button>
      <span v-if="savedPath" class="export-done">Saved to {{ savedPath }}</span>
    </div>
    <p v-if="savedPath && leftOut.length" class="s-note warn">
      {{ leftOut.length }} {{ leftOut.length === 1 ? 'page' : 'pages' }} couldn’t be read and {{ leftOut.length === 1 ? 'was' : 'were' }} left out:
      {{ leftOut.join(', ') }}. The README in the zip lists {{ leftOut.length === 1 ? 'it' : 'them' }} too.
    </p>
    <p class="export-note">
      Markdown opens in any text editor and imports into most writing apps.
      To bring your writing back into Superelectric Ink later,
      <button type="button" class="export-alt" :disabled="exporting !== null || disabled" @click="handleExport('sei')">
        {{ exporting === 'sei' ? progressLabel : 'export a .sei file' }}</button>
      too.
    </p>
    <div v-if="exporting" class="export-platen" role="progressbar" :aria-valuetext="progressLabel">
      <MoireCanvas pattern="platen" :step="5" :params="{ feed }" :ease="450" />
    </div>
    <p v-if="exportError" class="s-note error">{{ exportError }}</p>
    <label v-if="!savedPath" class="s-check">
      <input v-model="skipped" type="checkbox" :disabled="exporting !== null || disabled" @change="update" />
      I don’t need a copy of my writing
    </label>
  </div>
</template>

<style scoped>
.export-step {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.export-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.export-platen {
  --moire-line: color-mix(in srgb, var(--accent) 70%, transparent);
  position: relative;
  height: 22px;
  max-width: 360px;
  overflow: hidden;
  border: 1px solid var(--s-rule-strong, var(--surface-border));
  border-radius: 4px;
}

.export-done {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--s-ink-soft, var(--text-muted));
  font-size: 12px;
}

.export-note {
  margin: 0;
  color: var(--s-muted, var(--text-muted));
  font-size: 12px;
  line-height: 1.5;
}

.export-alt {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent-light, var(--accent));
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.export-alt:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
